import React, { useState } from 'react';
import { updateChannel, removeChannel } from '../../../services/channelService';
import { useAuth } from '../../../context/AuthContext';
import toast from 'react-hot-toast';

export default function ChannelSettingsPanel({ channel, onClose, onUpdateSuccess }) {
    const [activeTab, setActiveTab] = useState('general');
    const [channelName, setChannelName] = useState(channel.name);
    const [description, setDescription] = useState(channel.description);
    const [topic, setTopic] = useState(channel.topic || '');
    const [isUpdating, setIsUpdating] = useState(false);
    const [notifLevel, setNotifLevel] = useState('all');
    const [isMuted, setIsMuted] = useState(channel.muted || false);

    const { user } = useAuth();
    const role = user?.user_role?.role || user?.role || '';
    const isPrivileged = role === 'admin' || role === 'superadmin';

    const handleUpdate = async () => {
        setIsUpdating(true);
        try {
            await updateChannel(channel._id || channel.id, {
                name: channelName,
                description,
                topic
            });
            toast.success('Channel updated');
            onUpdateSuccess && onUpdateSuccess();
            onClose();
        } catch (error) {
            console.error("Error updating channel:", error);
            toast.error('Failed to update channel');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = () => {
        toast((t) => (
            <div className="flex flex-col gap-2">
                <p className="text-sm font-semibold">Delete <span className="font-bold">#{channel.name}</span>?</p>
                <p className="text-xs text-gray-400">This action cannot be undone.</p>
                <div className="flex gap-2 mt-1">
                    <button
                        onClick={() => { toast.dismiss(t.id); confirmDelete(); }}
                        className="flex-1 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-bold transition-colors"
                    >
                        Delete
                    </button>
                    <button
                        onClick={() => toast.dismiss(t.id)}
                        className="flex-1 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        ), { duration: Infinity });
    };

    const confirmDelete = async () => {
        setIsUpdating(true);
        try {
            await removeChannel(channel._id || channel.id);
            toast.success('Channel deleted');
            onUpdateSuccess && onUpdateSuccess();
            onClose();
        } catch (error) {
            console.error("Error deleting channel:", error);
            toast.error('Failed to delete channel');
        } finally {
            setIsUpdating(false);
        }
    };

    const tabs = [
        { id: 'general', label: 'General' },
        { id: 'permissions', label: 'Permissions' },
        { id: 'notifications', label: 'Notifications' },
        { id: 'integrations', label: 'Integrations' },
    ];

    return (
        <aside className="w-full md:w-[300px] h-full bg-[#1C1C1A] border-l border-white/5 flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                <h3 className="text-sm font-bold text-white uppercase tracking-widest">Channel Settings</h3>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors"
                >
                    ✕
                </button>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap border-b border-white/5 bg-black/20">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3 py-3 text-[10px] font-bold uppercase tracking-wider transition-all border-b-2 ${activeTab === tab.id
                            ? 'border-stone-500 text-stone-400 bg-white/5'
                            : 'border-transparent text-gray-500 hover:text-gray-300'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">

                {/* GENERAL */}
                {activeTab === 'general' && (
                    <div className="space-y-6">

                        {/* Channel Name */}
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 mb-2 block uppercase tracking-widest">Channel Name</label>
                            <div className="flex items-center gap-2 bg-[#161614] border border-white/5 rounded-xl px-3 py-2.5 focus-within:border-stone-500/50 transition-colors">
                                <span className="text-gray-600 font-bold">#</span>
                                <input
                                    value={channelName}
                                    onChange={e => setChannelName(e.target.value)}
                                    className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-gray-700"
                                    placeholder="channel-name"
                                />
                            </div>
                        </div>

                        {/* Topic */}
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 mb-2 block uppercase tracking-widest">Topic</label>
                            <input
                                value={topic}
                                onChange={e => setTopic(e.target.value)}
                                placeholder="Add a topic..."
                                className="w-full bg-[#161614] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-stone-500/50 transition-colors"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="text-[10px] font-bold text-gray-500 mb-2 block uppercase tracking-widest">Description</label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="What is this channel for?"
                                className="w-full bg-[#161614] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white h-24 resize-none outline-none focus:border-stone-500/50 transition-colors"
                            />
                        </div>

                        {/* Danger Zone */}
                        <div className="border-t border-white/5 pt-6 mt-8">
                            <p className="text-[10px] font-black text-red-500/50 mb-4 uppercase tracking-[0.2em]">Danger Zone</p>

                            <button className="w-full mb-3 py-2.5 rounded-xl border border-yellow-500/20 text-yellow-500/80 hover:bg-yellow-500/10 text-xs font-bold transition-all uppercase tracking-widest">
                                Archive Channel
                            </button>

                            {isPrivileged && (
                                <button
                                    onClick={handleDelete}
                                    className="w-full py-2.5 rounded-xl border border-red-500/20 text-red-500/80 hover:bg-red-500/10 text-xs font-bold transition-all uppercase tracking-widest"
                                >
                                    Delete Channel
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* PERMISSIONS */}
                {activeTab === 'permissions' && (
                    <div className="space-y-4">
                        {[
                            'Post messages',
                            'Add reactions',
                            'Upload files',
                            'Pin messages',
                            'Invite members',
                            'Edit channel info',
                        ].map((item, i) => (
                            <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                                <span className="text-sm text-white font-medium">{item}</span>
                                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-tighter bg-white/5 px-2 py-0.5 rounded">Admins only</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* NOTIFICATIONS */}
                {activeTab === 'notifications' && (
                    <div className="space-y-6">

                        {/* Mute */}
                        <div className="flex justify-between items-center p-4 bg-[#161614] border border-white/5 rounded-2xl">
                            <span className="text-sm font-bold text-white uppercase tracking-wider">Mute Channel</span>
                            <button
                                onClick={() => setIsMuted(!isMuted)}
                                className={`w-10 h-5 rounded-full transition-all relative ${isMuted ? 'bg-stone-500 shadow-stone-500/20 shadow-lg' : 'bg-white/10'
                                    }`}
                            >
                                <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all transform ${isMuted ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>

                        {/* Options */}
                        <div className="space-y-2">
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">Notify me for</p>
                            {['all', 'mentions', 'none'].map(opt => (
                                <button
                                    key={opt}
                                    onClick={() => setNotifLevel(opt)}
                                    className={`w-full p-3 rounded-xl border text-sm font-bold capitalize transition-all ${notifLevel === opt
                                        ? 'border-stone-500 bg-stone-500/10 text-stone-400'
                                        : 'border-white/5 text-gray-400 hover:bg-white/5'
                                        }`}
                                >
                                    {opt === 'none' ? 'Nothing' : opt === 'all' ? 'Every Message' : 'Mentions Only'}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* INTEGRATIONS */}
                {activeTab === 'integrations' && (
                    <div className="space-y-3">
                        {['GitHub', 'Jira', 'Figma', 'Datadog'].map((name, i) => (
                            <div key={i} className="flex justify-between items-center p-4 bg-[#161614] border border-white/5 rounded-2xl hover:bg-white/5 transition-all cursor-pointer group">
                                <span className="text-sm font-bold text-white">{name}</span>
                                <button className="text-[10px] font-black text-stone-400 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                    Connect
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Save */}
            {activeTab === 'general' && (
                <div className="p-4 border-t border-white/5 bg-black/20">
                    <button
                        onClick={handleUpdate}
                        disabled={isUpdating}
                        className={`w-full py-3 rounded-xl text-white font-bold text-sm transition-all shadow-xl ${isUpdating ? 'bg-gray-700 cursor-not-allowed' : 'bg-stone-600 hover:bg-stone-500 shadow-stone-600/20 active:scale-[0.98]'
                            }`}
                    >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            )}
        </aside>
    );
}